import { clamp01, snapToPixel } from '../util/math.js';
import { catmullRomPoint, pointDistance, movePointTowards } from '../util/interpolation.js';
import { playerPixelsPerFrame } from './speed.js';
import type { Point } from '../util/interpolation.js';
import type { FieldPlayer, BallPoint } from '../player/model.js';
import { clampRange } from '../util/math.js';

export interface PathSegment {
  type: 'path';
  startFrame: number;
  endFrame: number;
  sample: string;
  moving: boolean;
  points: Point[];
}

export interface HoldSegment {
  type: 'hold';
  startFrame: number;
  endFrame: number;
  moving: boolean;
  point: Point;
}

export type TrackSegment = PathSegment | HoldSegment;

export interface MotionState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  speedRating: number;
}

export interface LiveFrame {
  offensePlayers: FieldPlayer[];
  defensePlayers: FieldPlayer[];
  ball: BallPoint;
}

export function compactPathPoints(points: Point[]): Point[] {
  return points.filter((point, index) => {
    if (index === 0) return true;
    const previous = points[index - 1];
    return previous.x !== point.x || previous.y !== point.y;
  });
}

export function createPathSegment(startFrame: number, endFrame: number, points: Point[], sample: string = 'polyline', moving: boolean = true): PathSegment {
  return {
    type: 'path',
    startFrame,
    endFrame,
    sample,
    moving,
    points: compactPathPoints(points.map((p) => ({ x: snapToPixel(p.x), y: snapToPixel(p.y) }))),
  };
}

export function createHoldSegment(startFrame: number, endFrame: number, point: Point, moving: boolean = false): HoldSegment {
  return {
    type: 'hold',
    startFrame,
    endFrame,
    moving,
    point: { x: snapToPixel(point.x), y: snapToPixel(point.y) },
  };
}

export function finalPointForSegment(segment: TrackSegment): Point {
  return segment.type === 'hold'
    ? segment.point
    : segment.points[segment.points.length - 1] || { x: 0, y: 0 };
}

export function sampleTrackPoint(segment: TrackSegment, amount: number): Point {
  if (segment.type === 'hold') return segment.point;
  return segment.sample === 'spline'
    ? catmullRomPoint(segment.points, amount, 'constant')
    : polylinePointAt(segment.points, amount);
}

export function evaluateTrack(segments: TrackSegment[] | null, frame: number, fallbackPoint: Point): Point & { moving: boolean } {
  let point: Point = { x: snapToPixel(fallbackPoint.x), y: snapToPixel(fallbackPoint.y) };

  for (const segment of segments || []) {
    if (frame < segment.startFrame) {
      return { ...point, moving: false };
    }
    if (frame <= segment.endFrame) {
      if (segment.type === 'hold') {
        return { ...segment.point, moving: segment.moving };
      }
      const duration = Math.max(1, segment.endFrame - segment.startFrame);
      const amount = clamp01((frame - segment.startFrame) / duration);
      const next = sampleTrackPoint(segment, amount);
      return {
        x: snapToPixel(next.x),
        y: snapToPixel(next.y),
        moving: segment.moving && frame > segment.startFrame && frame < segment.endFrame,
      };
    }
    point = finalPointForSegment(segment);
  }
  return { ...point, moving: false };
}

export function evaluateFlowingTrack(
  segments: TrackSegment[] | null,
  frame: number,
  fallbackPoint: Point,
  options: {
    lookahead?: number;
    settleBlend?: number;
    driftSign?: number;
    driftScale?: number;
    minDrift?: number;
    maxDrift?: number;
    nearDriftDistance?: number;
    farDriftDistance?: number;
  } = {},
): Point & { moving: boolean } {
  let point: Point = { x: snapToPixel(fallbackPoint.x), y: snapToPixel(fallbackPoint.y) };

  for (const segment of segments || []) {
    if (frame < segment.startFrame) {
      return { ...point, moving: false };
    }
    if (frame <= segment.endFrame) {
      if (segment.type === 'hold') {
        return { ...segment.point, moving: segment.moving };
      }
      const duration = Math.max(1, segment.endFrame - segment.startFrame);
      const amount = clamp01((frame - segment.startFrame) / duration);
      const next = flowingGuideTarget(segment.points, amount, segment.sample, options);
      return {
        x: snapToPixel(next.x),
        y: snapToPixel(next.y),
        moving: segment.moving && frame > segment.startFrame && frame < segment.endFrame,
      };
    }
    point = finalPointForSegment(segment);
  }
  return { ...point, moving: false };
}

export function polylinePointAt(points: Point[], amount: number): Point {
  if (!points.length) return { x: 0, y: 0 };
  if (points.length === 1) return { x: snapToPixel(points[0].x), y: snapToPixel(points[0].y) };

  const progress = clamp01(amount);
  let totalDist = 0;
  for (let i = 1; i < points.length; i++) {
    totalDist += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  if (totalDist <= 0) return { x: snapToPixel(points[points.length - 1].x), y: snapToPixel(points[points.length - 1].y) };

  let remaining = totalDist * progress;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const cur = points[i];
    const segDist = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    if (remaining <= segDist || i === points.length - 1) {
      const ratio = segDist <= 0 ? 1 : clamp01(remaining / segDist);
      return {
        x: snapToPixel(prev.x + (cur.x - prev.x) * ratio),
        y: snapToPixel(prev.y + (cur.y - prev.y) * ratio),
      };
    }
    remaining -= segDist;
  }
  return { x: snapToPixel(points[points.length - 1].x), y: snapToPixel(points[points.length - 1].y) };
}

export function createMotionState(player: FieldPlayer): MotionState {
  return { x: player.x, y: player.y, vx: 0, vy: 0, speedRating: player.speedRating || 75 };
}

export function velocityMagnitude(state: MotionState): number {
  return Math.hypot(state.vx || 0, state.vy || 0);
}

export function dampMotionState(state: MotionState, amount: number = 0.78): MotionState {
  state.vx *= amount;
  state.vy *= amount;
  state.x += state.vx;
  state.y += state.vy;
  return state;
}

export function guidePointAt(points: Point[] | null, amount: number, sample: string = 'polyline'): Point {
  if (!points?.length) return { x: 0, y: 0 };
  return sample === 'spline'
    ? catmullRomPoint(points, amount, 'constant')
    : polylinePointAt(points, amount);
}

export function flowingGuideTarget(
  points: Point[] | null,
  amount: number,
  sample: string = 'polyline',
  options: {
    lookahead?: number;
    settleBlend?: number;
    driftSign?: number;
    driftScale?: number;
    minDrift?: number;
    maxDrift?: number;
    nearDriftDistance?: number;
    farDriftDistance?: number;
  } = {},
): Point {
  const base = guidePointAt(points, amount, sample);
  if (!points?.length) {
    return base;
  }

  const progress = clamp01(amount);
  const lookahead = clampRange(options.lookahead ?? 0.06, 0.01, 0.2);
  const settleBlend = clampRange(options.settleBlend ?? 0.56, 0, 1);
  const ahead = guidePointAt(points, clamp01(progress + lookahead), sample);
  const behind = guidePointAt(points, clamp01(progress - lookahead * 0.66), sample);
  const tangentX = ahead.x - behind.x;
  const tangentY = ahead.y - behind.y;
  const tangentMagnitude = Math.hypot(tangentX, tangentY);

  let x = snapToPixel(base.x + (ahead.x - base.x) * settleBlend);
  let y = snapToPixel(base.y + (ahead.y - base.y) * settleBlend);

  const driftSign = Math.sign(options.driftSign || 0);
  const driftScale = options.driftScale ?? 0;
  if (driftSign !== 0 && driftScale > 0 && tangentMagnitude > 0.001) {
    const nearDistance = options.nearDriftDistance ?? 10;
    const farDistance = Math.max(nearDistance + 1, options.farDriftDistance ?? 34);
    const driftDistance = pointDistance(base, ahead);
    const driftBlend = clamp01((driftDistance - nearDistance) / Math.max(1, farDistance - nearDistance));
    const minDrift = options.minDrift ?? 0.4;
    const maxDrift = options.maxDrift ?? 3.4;
    const driftWindow = Math.sin(progress * Math.PI);
    const normalX = -tangentY / tangentMagnitude;
    const normalY = tangentX / tangentMagnitude;
    const driftAmount = (minDrift + (maxDrift - minDrift) * driftBlend) * driftScale * driftWindow * driftSign;
    x = snapToPixel(x + normalX * driftAmount);
    y = snapToPixel(y + normalY * driftAmount);
  }

  return { x, y };
}

export function projectedPoint(state: MotionState, framesAhead: number = 0, offsetX: number = 0, offsetY: number = 0): Point {
  return {
    x: snapToPixel(state.x + (state.vx || 0) * framesAhead + offsetX),
    y: snapToPixel(state.y + (state.vy || 0) * framesAhead + offsetY),
  };
}

export function curvedApproachTarget(
  current: Point,
  target: Point,
  lateralSign: number,
  options: {
    nearDistance?: number;
    farDistance?: number;
    minOffset?: number;
    maxOffset?: number;
    xBlend?: number;
    yBlend?: number;
    scale?: number;
  } = {},
): Point {
  const distance = pointDistance(current, target);
  const nearDistance = options.nearDistance ?? 18;
  if (distance <= nearDistance) {
    return { x: snapToPixel(target.x), y: snapToPixel(target.y) };
  }

  const farDistance = Math.max(nearDistance + 1, options.farDistance ?? 84);
  const distanceBlend = clamp01((distance - nearDistance) / Math.max(1, farDistance - nearDistance));
  const scale = options.scale ?? 1;
  const minOffset = (options.minOffset ?? 4) * scale;
  const maxOffset = (options.maxOffset ?? 16) * scale;
  const shoulderOffset = (minOffset + (maxOffset - minOffset) * distanceBlend) * (lateralSign || 0);
  const xBlend = options.xBlend ?? 0.68;
  const yBlend = options.yBlend ?? 0.56;

  return {
    x: snapToPixel(current.x + (target.x - current.x) * xBlend),
    y: snapToPixel(current.y + (target.y - current.y) * yBlend + shoulderOffset),
  };
}

export function steerMotionState(state: MotionState, target: Point | null, options: {
  family?: string; scale?: number; accelRatio?: number; drag?: number;
  arriveDistance?: number; stopDistance?: number; inertia?: number;
} = {}): MotionState {
  const family = options.family || 'run';
  const scale = options.scale ?? 1;
  const maxSpeed = playerPixelsPerFrame(state.speedRating || 75, family, scale);
  const accel = Math.max(0.2, maxSpeed * (options.accelRatio ?? 0.34));
  const drag = options.drag ?? 0.88;
  const arriveDistance = options.arriveDistance ?? Math.max(10, maxSpeed * 2.8);
  const stopDistance = options.stopDistance ?? 2;

  if (!target) return dampMotionState(state, drag);

  const deltaX = target.x - state.x;
  const deltaY = target.y - state.y;
  const distance = Math.hypot(deltaX, deltaY);

  if (distance <= stopDistance) {
    state.x = target.x;
    state.y = target.y;
    state.vx *= drag * 0.7;
    state.vy *= drag * 0.7;
    return state;
  }

  const desiredSpeed = distance < arriveDistance
    ? Math.max(maxSpeed * 0.34, maxSpeed * (distance / arriveDistance))
    : maxSpeed;
  const desiredVX = (deltaX / distance) * desiredSpeed;
  const desiredVY = (deltaY / distance) * desiredSpeed;
  state.vx += clampRange(desiredVX - state.vx, -accel, accel);
  state.vy += clampRange(desiredVY - state.vy, -accel, accel);

  const speed = velocityMagnitude(state);
  if (speed > maxSpeed) {
    const cap = maxSpeed / speed;
    state.vx *= cap;
    state.vy *= cap;
  }

  state.vx *= options.inertia ?? 0.96;
  state.vy *= options.inertia ?? 0.96;
  state.x += state.vx;
  state.y += state.vy;
  return state;
}

export function playersFromMotionStates(basePlayers: FieldPlayer[], motionStates: Map<string, MotionState>): FieldPlayer[] {
  return basePlayers.map((player) => {
    const ms = motionStates.get(player.role);
    if (!ms) return { ...player };
    return { ...player, x: snapToPixel(ms.x), y: snapToPixel(ms.y), moving: velocityMagnitude(ms) > 0.16 };
  });
}

export function freezeLiveFrame(offensePlayers: FieldPlayer[], defensePlayers: FieldPlayer[], ball: BallPoint): LiveFrame {
  return {
    offensePlayers: offensePlayers.map((p) => ({ ...p, moving: false })),
    defensePlayers: defensePlayers.map((p) => ({ ...p, moving: false })),
    ball: { ...ball },
  };
}

export function cloneLiveFrame(frame: LiveFrame): LiveFrame {
  return {
    offensePlayers: frame.offensePlayers.map((p) => ({ ...p })),
    defensePlayers: frame.defensePlayers.map((p) => ({ ...p })),
    ball: { ...frame.ball },
  };
}

export function spreadTargetAwayFromPeers(target: Point, peers: Point[], minDistance: number = 20, xWeight: number = 0.35): Point {
  let adjustX = 0;
  let adjustY = 0;
  for (const peer of peers) {
    const dx = target.x - peer.x;
    const dy = target.y - peer.y;
    const dist = Math.hypot(dx, dy);
    if (dist > 0 && dist < minDistance) {
      const push = (minDistance - dist) / minDistance;
      adjustX += (dx === 0 ? 0.5 : Math.sign(dx)) * push * minDistance * xWeight;
      adjustY += (dy === 0 ? 0.5 : Math.sign(dy)) * push * minDistance * (1 - xWeight);
    }
  }
  return { x: snapToPixel(target.x + adjustX), y: snapToPixel(target.y + adjustY) };
}

export function roleSpacingDistance(role: string, family: string = 'run'): number {
  if (role.startsWith('LB') || role.startsWith('CONTAIN')) return family === 'run' ? 14 : 16;
  if (role.startsWith('CB') || role.startsWith('FS') || role.startsWith('SS') || role.startsWith('SAFETY')) return 18;
  if (role.startsWith('DE') || role.startsWith('DT') || role.startsWith('EDGE') || role.startsWith('RUSH')) return 12;
  return 14;
}
